import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { IDeliveryType, IOtherControl, IPackage } from '../../models/configuration/plugin/IConfiguration';
import expressionService from '../../services/expressionService';
import { IEvaluatedPackage, IEvaluatedDelivery, IEvaluatedOtherControl } from '../../models/IExpressionValidation';
import { enhanceFieldIdValuePairs } from '../../utils/expressionEvaluationHelper';

const createValidConfigDataThunk = (type: string) => {
  return createAsyncThunk(
    type,
    async (dataset: any, { getState, rejectWithValue }) => {
      try {
        const state = getState() as RootState;
        const token = state.appSlice.accessToken;
        const fieldIdValueIdPairsFromOrigin: { [key: string]: string } = state.appSlice.fieldIdValuePairs ?? {};
        const selectedPackage = state.appSlice.selectedPackage;
        const selectedDeliveryMethods = state.appSlice.orderState.selectedDeliveryMethods;
        
        // Enhance field values with selected package and delivery methods for expression evaluation
        const enhancedFieldIdValuePairs = enhanceFieldIdValuePairs(fieldIdValueIdPairsFromOrigin, selectedPackage, selectedDeliveryMethods);
        
        const expService = new expressionService();

        const typeToMethodMap: { [key: string]: (token: string | null, dataset: IPackage[] | IDeliveryType[] | IOtherControl[],
            fieldIdValueIdPairs: { [key: string]: string }) => Promise<any>
        } = {
          'datasets/fetchEvaluatedPackages': async (token, dataset, fieldIdValueIdPairs) => {
            return await expService.CanShowOnForm(token, dataset as IPackage[], fieldIdValueIdPairs);
          },
          'datasets/fetchEvaluatedDeliveryMethods': async (token, dataset, fieldIdValueIdPairs) => {
            return await expService.CanShowOnForm(token, dataset as IDeliveryType[], fieldIdValueIdPairs);
          },
          'datasets/fetchEvaluatedOtherControls': async (token, dataset, fieldIdValueIdPairs) => {
            return await expService.CanShowOnForm(token, dataset as IOtherControl[], fieldIdValueIdPairs);
          }
        };

        const method = typeToMethodMap[type];
        if (!method) {
          throw new Error(`No method found for type: ${type}`);
        }
        return await method(token, dataset, enhancedFieldIdValuePairs);
      }
      catch (error: any) {
        return rejectWithValue(error.message || 'An error occurred');
      }
    })
}

export const fetchEvaluatedPackages = createValidConfigDataThunk('datasets/fetchEvaluatedPackages');
export const fetchEvaluatedDeliveryMethods = createValidConfigDataThunk('datasets/fetchEvaluatedDeliveryMethods');
export const fetchEvaluatedOtherControls = createValidConfigDataThunk('datasets/fetchEvaluatedOtherControls');

interface ExpressionValidationState {
  packages: IPackage[] | null;
  deliveryMethods: IDeliveryType[] | null;
  otherControls: IOtherControl[] | null;
  evaluatedPackages: IEvaluatedPackage[] | null;
  evaluatedDeliveryMethods: IEvaluatedDelivery[] | null;
  evaluatedOtherControls: IEvaluatedOtherControl[] | null;
  loading: boolean;
  error: string | null;
}

const initialState: ExpressionValidationState = {
  packages: null,
  deliveryMethods: null,
  otherControls: null,
  evaluatedPackages: null,
  evaluatedDeliveryMethods: null,
  evaluatedOtherControls: null,
  loading: false,
  error: null,
};


const importExpressionValidationSlice = createSlice({
  name: 'expressionDataSets',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvaluatedPackages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvaluatedPackages.fulfilled, (state, action: PayloadAction<IEvaluatedPackage[]>) => {
        state.loading = false;
        state.error = null;
        state.evaluatedPackages = action.payload;
      })
      .addCase(fetchEvaluatedPackages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to evaluate packages';
      })
      .addCase(fetchEvaluatedDeliveryMethods.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvaluatedDeliveryMethods.fulfilled, (state, action: PayloadAction<IEvaluatedDelivery[]>) => {
        state.loading = false;
        state.error = null;
        const incoming = action.payload;
        if (!state.evaluatedDeliveryMethods?.length) {
          state.evaluatedDeliveryMethods = incoming;
        } else {
          const incomingIds = new Set(
            incoming.map(r => r.validConfigType?.Id?.toLowerCase())
          );
          const preserved = state.evaluatedDeliveryMethods.filter(
            r => !incomingIds.has(r.validConfigType?.Id?.toLowerCase())
          );
          state.evaluatedDeliveryMethods = [...preserved, ...incoming];
        }
      })
      .addCase(fetchEvaluatedDeliveryMethods.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to evaluate delivery methods';
      })
      .addCase(fetchEvaluatedOtherControls.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvaluatedOtherControls.fulfilled, (state, action: PayloadAction<IEvaluatedOtherControl[]>) => {
        state.loading = false;
        state.error = null;
        state.evaluatedOtherControls = action.payload;
      })
      .addCase(fetchEvaluatedOtherControls.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to evaluate other controls';
      })
      .addCase('config_id_changed', () => initialState);
  },
});
export default importExpressionValidationSlice.reducer;
